import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { type FormEvent, useEffect, useState } from 'react'
import { AuthCard } from '@/components/auth-card'
import { Footer } from '@/components/footer'
import { NavBar } from '@/components/nav-bar'
import { TextInput } from '@/components/text-input'
import { getCurrentUser, PlatformApiError, signIn } from '@/lib/api'
import { friendlyErrorMessage } from '@/lib/errors'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/sign-in')({
  component: SignInPage,
})

function SignInPage() {
  const t = useTranslations('sign_in')
  const navigate = useNavigate()

  // Redirect to /account if already signed in.
  useEffect(() => {
    const ctrl = new AbortController()
    getCurrentUser(ctrl.signal)
      .then(() => void navigate({ to: '/account' }))
      .catch(() => {})
    return () => ctrl.abort()
  }, [navigate])

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Separate state for the "unverified email — we re-sent the
  // verification link" case. Distinct from `error` because the
  // UX is different (info banner, not error banner) and the user
  // needs different copy from a generic auth failure.
  const [resentForEmail, setResentForEmail] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setResentForEmail(null)
    setSubmitting(true)
    try {
      // Pass callbackURL so, if the user is unverified, better-auth
      // re-fires the verification email with a link that points
      // here after verify. The user then clicks the link, lands
      // on /account signed in.
      const callbackURL = `${window.location.origin}/account`
      await signIn({ email, password, callbackURL })
      void navigate({ to: '/account' })
    } catch (err) {
      // 403 with code EMAIL_NOT_VERIFIED is a special case —
      // render the "we re-sent the verification email" info
      // banner, not a generic error. better-auth auto-re-sends
      // the email (sendOnSignIn: true), so the failed sign-in
      // attempt IS the resend action.
      if (err instanceof PlatformApiError) {
        const body = err.body as { code?: string } | null
        if (err.status === 403 && body?.code === 'EMAIL_NOT_VERIFIED') {
          setResentForEmail(email)
          return
        }
        // 401 bad credentials → i18n friendly message (better
        // than the raw "Invalid email or password" from better-
        // auth, which we override to match marketing voice).
        if (err.status === 401) {
          setError(t('error_generic'))
          return
        }
      }
      // Everything else (429, 5xx, network) goes through the
      // shared classifier, which maps server errors to "on our
      // side, will be fixed" copy without leaking raw messages.
      setError(friendlyErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <NavBar />
      <main>
        <AuthCard
          title={t('title')}
          subtitle={t('subtitle')}
          footer={
            <span>
              {t('no_account')}{' '}
              <Link to="/sign-up" className="font-medium text-primary hover:underline">
                {t('sign_up_link')}
              </Link>
            </span>
          }
        >
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <TextInput
              label={t('email_label')}
              type="email"
              autoComplete="email"
              required
              placeholder={t('email_placeholder')}
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            <TextInput
              label={t('password_label')}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />

            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-xs font-medium text-muted-foreground hover:text-primary hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            {resentForEmail ? (
              <div className="rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm text-primary">
                <strong>Verify your email first.</strong> We've re-sent the verification link to{' '}
                <span className="font-mono">{resentForEmail}</span>. Click the link, then come back
                to sign in.
              </div>
            ) : null}

            {error ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className={cn(
                'w-full rounded-lg px-4 py-2.5 text-sm font-medium',
                'bg-primary text-primary-foreground shadow-md shadow-primary/20',
                'hover:opacity-90 transition-opacity',
                'disabled:opacity-60 disabled:cursor-not-allowed',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              )}
            >
              {submitting ? t('submitting') : t('submit')}
            </button>
          </form>
        </AuthCard>
      </main>
      <Footer />
    </>
  )
}
